import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { FC, useCallback, useMemo } from "react";
import { Box, HStack, MenuItemOption, MenuOptionGroup } from "@chakra-ui/react";
import { LeadNextActionDateFiltersOptions } from "constants/lead-filters-options";
import { LeadNextActionDateFilterEnum } from "enums/lead-next-action-date-filter.enum";
import { DateRangePickerController } from "components/date-picker/DateRangePickerController";
import { isEqual } from "lodash";
import { AccountLeadsFiltersStore } from "store/UserAccounts/UserAccount/AccountLeads/AccountLeadsFilters.store";
import { observer } from "mobx-react";
import { DropdownFilterMenuShell } from "components";
import { TSerializedDateRange } from "types/date.type";
import { LEAD_FILTER_ICON_SIZE } from "constants/lead-filter-icon-size";
import { LeadFilterHeaderCell } from "./LeadFilterHeaderCell";
import { ResetLeadFilters } from "./LeadFilterMenu/ResetLeadFilters";
import { faFilter } from "@fortawesome/pro-solid-svg-icons";

interface IProps {
  leadsFilterStore: AccountLeadsFiltersStore;
}

export const FilterLeadsNextActionDateHeader: FC<IProps> = observer(
  ({ leadsFilterStore }) => {
    const {
      pendingFilter: { nextActionDate: pendingNextActionDate },
      activeFilter,
    } = leadsFilterStore;

    const isOptionSelected = useMemo(() => {
      if (pendingNextActionDate.type !== LeadNextActionDateFilterEnum.ALL)
        return 1;

      return 0;
    }, [pendingNextActionDate]);

    const handleOptionSelect = useCallback(
      (value: string | string[]) => {
        leadsFilterStore.setPendingFilterNextActionDate({
          type: value as LeadNextActionDateFilterEnum,
        });
      },
      [leadsFilterStore]
    );

    const handleDatesChange = useCallback(
      (dates: TSerializedDateRange) => {
        leadsFilterStore.setPendingFilterNextActionDate({
          type: LeadNextActionDateFilterEnum.CUSTOM,
          from: dates.startDate || undefined,
          to: dates.endDate || undefined,
        });
      },
      [leadsFilterStore]
    );

    const onClose = useCallback(() => {
      if (!isEqual(pendingNextActionDate, activeFilter?.nextActionDate)) {
        leadsFilterStore.setActiveFromPendingFilter();
      }
    }, [leadsFilterStore, pendingNextActionDate, activeFilter?.nextActionDate]);

    return (
      <LeadFilterHeaderCell title={"Next Action"}>
        <DropdownFilterMenuShell
          icon={
            <FontAwesomeIcon icon={faFilter} fontSize={LEAD_FILTER_ICON_SIZE} />
          }
          badgeCount={isOptionSelected}
          onClose={onClose}
        >
          <HStack
            width={"100%"}
            justifyContent={"space-between"}
            gap={2}
            pl={5}
            pr={3}
            py={3}
          >
            <Box fontWeight={800} color={"leadpro.700"}>
              Next Action Date Range
            </Box>
            <ResetLeadFilters leadsFilterStore={leadsFilterStore} />
          </HStack>
          <HStack spacing={0} pr={2}>
            <MenuOptionGroup
              type="radio"
              value={pendingNextActionDate.type}
              onChange={handleOptionSelect}
            >
              {LeadNextActionDateFiltersOptions.map(option => (
                <MenuItemOption key={option.value} value={option.value}>
                  {option.label}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>
            <Box borderLeft={"1px solid"} borderColor={"gray.200"}>
              <DateRangePickerController
                startDate={pendingNextActionDate.from || null}
                endDate={pendingNextActionDate.to || null}
                onChange={handleDatesChange}
              />
            </Box>
          </HStack>
        </DropdownFilterMenuShell>
      </LeadFilterHeaderCell>
    );
  }
);
